import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { ShoppingCart, Search, Building2, Users2, Receipt, Link2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { EmptyState } from "@/components/intelligence/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ordenesCompraService } from "@/services/ordenes-compra.service";
import type { OrdenOC, OrdenesCompraFiltros } from "@/types";
import { formatCLP } from "@/lib/mock-data";
import { canalBadge, tipoMPLabel } from "@/lib/mercado-publico-catalog";


export const Route = createFileRoute("/ordenes-compra/")({
  head: () => ({
    meta: [
      { title: "Órdenes de Compra — LicitaPro" },
      {
        name: "description",
        content:
          "Órdenes de compra emitidas por organismos públicos: montos reales, proveedores y licitación de origen.",
      },
      { property: "og:title", content: "Órdenes de Compra — LicitaPro" },
      {
        property: "og:description",
        content: "Gasto público efectivamente ejecutado, con vínculo a la licitación de origen.",
      },
    ],
  }),
  component: OrdenesCompraPage,
});

const ORDENES: Array<{ value: OrdenOC; label: string }> = [
  { value: "fecha", label: "Más recientes" },
  { value: "monto", label: "Mayor monto" },
  { value: "organismo", label: "Organismo (A-Z)" },
];

const PAGE_SIZE = 25;

function fmtFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-CL", { timeZone: "America/Santiago" });
}

function OrdenesCompraPage() {
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [organismo, setOrganismo] = useState("");
  const [proveedor, setProveedor] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [montoMin, setMontoMin] = useState("");
  const [montoMax, setMontoMax] = useState("");
  const [conLicitacion, setConLicitacion] = useState<"todas" | "si" | "no">("todas");
  const [tipoCompra, setTipoCompra] = useState("todos");
  const [orden, setOrden] = useState<OrdenOC>("fecha");
  const [page, setPage] = useState(1);

  const filtros: OrdenesCompraFiltros = {
    q: q || undefined,
    organismo: organismo || undefined,
    proveedor: proveedor || undefined,
    tipoCompra: tipoCompra === "todos" ? undefined : tipoCompra,
    fechaDesde: fechaDesde || undefined,
    fechaHasta: fechaHasta || undefined,
    montoMin: montoMin ? Number(montoMin) : undefined,
    montoMax: montoMax ? Number(montoMax) : undefined,
    conLicitacion: conLicitacion === "todas" ? undefined : conLicitacion,
    orden,
    page,
    pageSize: PAGE_SIZE,
  };

  const listado = useQuery({
    queryKey: ["ordenes-compra", filtros],
    queryFn: () => ordenesCompraService.list(filtros),
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  // Siglas reales de canal desde la base: el filtro nunca ofrece opciones vacías.
  const canales = useQuery({
    queryKey: ["ordenes-compra", "facetas", "tipo-compra"],
    queryFn: () => ordenesCompraService.facetasTipoCompra(),
    staleTime: 30 * 60_000,
  });


  const items = listado.data?.items ?? [];
  const resumen = listado.data?.resumen;
  const totalPaginas = Math.max(1, Math.ceil((listado.data?.total ?? 0) / PAGE_SIZE));

  const kpis = [
    {
      label: "Monto emitido",
      value: resumen ? formatCLP(resumen.totalEmitidoCLP) : "—",
      icon: Receipt,
    },
    { label: "Órdenes", value: resumen ? resumen.ordenes.toLocaleString("es-CL") : "—", icon: ShoppingCart },
    { label: "Proveedores", value: resumen ? resumen.proveedores.toLocaleString("es-CL") : "—", icon: Users2 },
    {
      label: "Ticket promedio",
      value: resumen ? formatCLP(resumen.ticketPromedioCLP) : "—",
      icon: Building2,
    },
  ];

  return (
    <AppShell
      title="Órdenes de compra"
      subtitle="Lo que el Estado efectivamente pagó — datos oficiales de Mercado Público"
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Icon className="size-3.5" />
                {k.label}
              </div>
              {listado.isLoading ? (
                <Skeleton className="h-6 w-28" />
              ) : (
                <div className="text-xl font-semibold tabular-nums">{k.value}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="rounded-xl border border-border bg-card p-4 mb-4 space-y-3">
        <form
          className="flex flex-col md:flex-row gap-3 md:items-center"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setQ(search.trim());
          }}
        >
          <div className="relative flex-1">
            <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por código, nombre o licitación"
              className="pl-9"
              aria-label="Buscar orden de compra"
            />
          </div>
          <Select
            value={orden}
            onValueChange={(v) => {
              setPage(1);
              setOrden(v as OrdenOC);
            }}
          >
            <SelectTrigger className="md:w-52" aria-label="Ordenar por">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ORDENES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={tipoCompra}
            onValueChange={(v) => {
              setPage(1);
              setTipoCompra(v);
            }}
          >
            <SelectTrigger className="md:w-56" aria-label="Canal de compra">
              <SelectValue placeholder="Todos los canales" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los canales</SelectItem>
              {(canales.data ?? []).map((c) => (
                <SelectItem key={c.tipoCompra} value={c.tipoCompra}>
                  {`${canalBadge(c.tipoCompra).short} · ${c.tipoCompra} (${c.ordenes.toLocaleString("es-CL")})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select

            value={conLicitacion}
            onValueChange={(v) => {
              setPage(1);
              setConLicitacion(v as "todas" | "si" | "no");
            }}
          >
            <SelectTrigger className="md:w-52" aria-label="Vínculo con licitación">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las OC</SelectItem>
              <SelectItem value="si">Con licitación de origen</SelectItem>
              <SelectItem value="no">Sin licitación (CM / ágil)</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit">Buscar</Button>
        </form>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <Input
            value={organismo}
            onChange={(e) => {
              setPage(1);
              setOrganismo(e.target.value);
            }}
            placeholder="Organismo"
            aria-label="Filtrar por organismo"
          />
          <Input
            value={proveedor}
            onChange={(e) => {
              setPage(1);
              setProveedor(e.target.value);
            }}
            placeholder="Proveedor o RUT"
            aria-label="Filtrar por proveedor"
          />
          <Input
            type="date"
            value={fechaDesde}
            onChange={(e) => {
              setPage(1);
              setFechaDesde(e.target.value);
            }}
            aria-label="Fecha desde"
          />
          <Input
            type="date"
            value={fechaHasta}
            onChange={(e) => {
              setPage(1);
              setFechaHasta(e.target.value);
            }}
            aria-label="Fecha hasta"
          />
          <Input
            type="number"
            min={0}
            value={montoMin}
            onChange={(e) => {
              setPage(1);
              setMontoMin(e.target.value);
            }}
            placeholder="Monto mínimo"
            aria-label="Monto mínimo"
          />
          <Input
            type="number"
            min={0}
            value={montoMax}
            onChange={(e) => {
              setPage(1);
              setMontoMax(e.target.value);
            }}
            placeholder="Monto máximo"
            aria-label="Monto máximo"
          />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {listado.isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : listado.isError ? (
          <EmptyState
            icon={ShoppingCart}
            title="No pudimos cargar las órdenes de compra"
            description="Reintenta en unos segundos."
            action={<Button onClick={() => listado.refetch()}>Reintentar</Button>}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="Sin órdenes de compra"
            description="Ajusta los filtros o espera la próxima sincronización con Mercado Público."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Canal</TableHead>
                <TableHead>Organismo</TableHead>
                <TableHead>Proveedor</TableHead>

                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Emisión</TableHead>
                <TableHead>Licitación</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((oc) => (
                <TableRow key={oc.id}>
                  <TableCell className="align-top">
                    <Link
                      to="/ordenes-compra/$codigo"
                      params={{ codigo: oc.codigo }}
                      className="font-medium text-primary hover:underline"
                    >
                      {oc.codigo}
                    </Link>
                    <div className="text-xs text-muted-foreground line-clamp-1 max-w-[22rem]">
                      {oc.nombre}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    {oc.tipoCompra ? (
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${canalBadge(oc.tipoCompra).color}`}
                        title={tipoMPLabel(oc.tipoCompra)}
                      >
                        {canalBadge(oc.tipoCompra).canal
                          ? canalBadge(oc.tipoCompra).short
                          : oc.tipoCompra}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="align-top max-w-[16rem]">
                    <div className="line-clamp-2 text-sm">{oc.organismo}</div>
                  </TableCell>


                  <TableCell className="align-top max-w-[14rem]">
                    <div className="line-clamp-2 text-sm">{oc.proveedorNombre ?? "—"}</div>
                    <div className="text-xs text-muted-foreground">{oc.proveedorRut ?? ""}</div>
                  </TableCell>
                  <TableCell className="align-top text-right tabular-nums">
                    {oc.totalCLP != null ? (
                      formatCLP(oc.totalCLP)
                    ) : (
                      <span
                        className="text-xs text-muted-foreground"
                        title="Mercado Público aún no entregó los montos de esta OC; se completan en la próxima sincronización."
                      >
                        Pendiente de sincronizar
                      </span>
                    )}
                    {oc.moneda !== "CLP" && (
                      <div className="text-xs text-muted-foreground">{oc.moneda}</div>
                    )}
                  </TableCell>
                  <TableCell className="align-top text-sm">{fmtFecha(oc.fechaEnvio)}</TableCell>
                  <TableCell className="align-top text-sm">
                    {oc.licitacionCodigo && oc.licitacionExiste ? (
                      <Link
                        to="/licitaciones/$id"
                        params={{ id: oc.licitacionCodigo }}
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                      >
                        <Link2 className="size-3.5" />
                        {oc.licitacionCodigo}
                        {oc.vinculoOrigen === "sugerido" && (
                          <span className="text-[10px] px-1 rounded bg-muted text-muted-foreground">
                            sugerido
                          </span>
                        )}
                      </Link>
                    ) : oc.licitacionCodigo ? (
                      <Link
                        to="/ordenes-compra/$codigo"
                        params={{ codigo: oc.codigo }}
                        className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                        title="La licitación de origen todavía no está en nuestros datos maestros"
                      >
                        {oc.licitacionCodigo}
                        <span className="text-[10px] px-1 rounded bg-muted text-muted-foreground">
                          no sincronizada
                        </span>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">Sin licitación</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {totalPaginas > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">
            Página {page} de {totalPaginas}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPaginas}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  );
}
